import Link from '@/components/Link'
import Tag from '@/components/Tag'
import siteMetadata from '@/data/siteMetadata'
import { Authors, Blog, allAuthors } from 'contentlayer/generated'
import Image from 'next/image'
import { CoreContent } from 'pliny/utils/contentlayer'
import { formatDate } from 'pliny/utils/formatDate'

export default function BlogCardHorizontal({
  post,
  author,
}: {
  post: Blog | CoreContent<Blog>
  author?: Authors
}) {
  const { slug, date, title, summary, tags, images, authors } = post
  const firstAuthor = allAuthors.find(
    (p) => p.slug === (authors && authors.length ? authors[0] : 'default')
  ) as Authors

  return (
    <div className="container">
      <div className="lg:-mx-6 lg:flex lg:items-center">
        <div className="relative h-72 w-full overflow-hidden rounded-xl border-[0.5px] border-gray-300/40 bg-clip-border lg:mx-6 lg:w-1/2">
          <Link
            className="relative block h-72 w-full overflow-hidden rounded-xl bg-clip-border"
            href={`/blog/${slug}`}
            aria-label={`Leggi ${title}`}
          >
            <Image
              className="h-72 w-full cursor-pointer object-cover transition-all duration-700 hover:scale-110"
              src={
                Array.isArray(images)
                  ? images[0]
                  : '/static/images/placeholders/placeholder-squared.png'
              }
              alt={'Immagine che descrive:' + title}
              sizes="auto"
              fill
              priority
            />
          </Link>
        </div>

        <div className="mt-2 flex min-h-full flex-col items-start justify-start lg:mt-0 lg:h-72 lg:w-1/2">
          <div className="flex-1">
            <p className="block text-2xl font-semibold text-gray-800 dark:text-white md:text-3xl">
              {title}
            </p>
            <div className="mt-4 flex flex-wrap">
              {tags.map((tag) => (
                <Tag key={tag} text={tag} />
              ))}
            </div>

            <p className=" multiline-truncate prose mt-3 max-w-none text-sm text-gray-800 dark:text-gray-400">
              {summary}
            </p>
            <button className="btn group mt-3 flex items-center bg-transparent text-base font-medium leading-6 tracking-wide">
              <Link
                href={`/blog/${slug}`}
                aria-label={`Leggi ${title}`}
                className="relative pb-1 pr-2 text-secondary-600 group-hover:text-secondary-500 dark:text-primary-500 dark:group-hover:text-primary-400"
              >
                <span className="block">
                  Leggi di più
                  <span className="after:absolute after:bottom-0 after:left-0 after:block after:h-[2px] after:w-full after:origin-bottom-right after:scale-x-0 after:bg-secondary-500 after:transition-transform after:duration-500 after:ease-out after:content-[''] group-hover:after:origin-bottom-left group-hover:after:scale-x-100 dark:after:bg-primary-600"></span>
                </span>
              </Link>

              <svg
                viewBox="0 0 46 16"
                height="8"
                width="12"
                id="arrow-horizontal-3"
                className="mb-1 ml-1 -translate-x-2 fill-secondary-500 transition-all duration-300 ease-out group-hover:fill-primary-400 dark:fill-primary-400 dark:group-hover:text-secondary-500"
              >
                <path
                  transform="translate(30)"
                  d="M8,0,6.545,1.455l5.506,5.506H-30V9.039H12.052L6.545,14.545,8,16l8-8Z"
                  data-name="Path 13"
                  id="Path_13"
                ></path>
              </svg>
            </button>
          </div>
          {firstAuthor && (
            <div className="my-2 mt-4 flex w-full items-center justify-between">
              <div className="flex items-center ">
                <div className="relative h-10 w-10 rounded-full bg-slate-600">
                  <Image
                    className="static h-10 w-10 rounded-full object-cover"
                    sizes="auto"
                    src={firstAuthor.avatar as string}
                    alt={firstAuthor.name}
                    fill
                  />
                </div>
                <dl className="ml-2 whitespace-nowrap text-sm font-medium leading-5">
                  <dt className="sr-only">Name</dt>
                  <dd className="text-gray-900 dark:text-gray-100">{firstAuthor.name}</dd>
                  <dt className="sr-only">LinkedIn</dt>
                  <dd>
                    {firstAuthor.linkedin && (
                      <Link
                        href={firstAuthor.linkedin as string}
                        className={
                          'text-secondary-600 hover:text-secondary-500 dark:text-primary-500 dark:hover:text-primary-400'
                        }
                      >
                        {firstAuthor.occupation}
                      </Link>
                    )}
                  </dd>
                </dl>
              </div>
              <div className="flex h-full items-end">
                <dl>
                  <dt className="sr-only">Published on</dt>
                  <dd className="text-sm font-medium leading-6 text-gray-500 dark:text-gray-400">
                    <time dateTime={date}>{formatDate(date, siteMetadata.locale)}</time>
                  </dd>
                </dl>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
